// @ts-nocheck - Implicit any parameters, unused variables, and FC type mismatch
import dayjs from 'dayjs';
import {
  Cell,
  Color,
  DynamicTable,
  Icons,
  IconWrapper,
  LinkAnimatedUnderline,
  RddkTheme,
  TableData,
  Tooltip,
} from 'db-npm-e4sc-rd-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatAsCurrency } from '../../shared/utils/currency';
import { TableCellRender } from './TableCellRender';
import { OpenOffer } from './types';

type TableFastforrentetProps = {
  data: OpenOffer[];
  handleClick: any;
};

export const TableFastforrentet: React.FC<TableFastforrentetProps> = ({ data, handleClick }) => {
  const { t } = useTranslation();
  const tableData = React.useMemo((): TableData => {
    if (!data || data.length <= 0) {
      return [];
    }

    const date1 = data[0]?.prices?.[1]?.date ? dayjs(data[0].prices?.[1].date).format('DD.MM.YYYY') : '';
    const date2 = data[0]?.prices?.[2]?.date ? dayjs(data[0].prices?.[2].date).format('DD.MM.YYYY') : '';
    const date3 = data[0]?.prices?.[3]?.date ? dayjs(data[0].prices?.[3].date).format('DD.MM.YYYY') : '';

    return [
      // Header row:
      [
        {
          style: {
            width: 250,
          },
          value: 'Obligationslån',
          alignVertically: 'middle',
          minWidth: 170,
        },

        // This column is here match "bond closed" column so sorting works (sorting functions uses the index of the header as the indes for the data column)
        {
          style: {
            borderLeft: 0,
            width: 120,
          },
          value: null,
          alignVertically: 'middle',
        },
        {
          value: 'Aktuel kurs',
          sortable: true,
          alignVertically: 'middle',
          minWidth: 160,
        },
        {
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-fixed-tooltip-aktuelkurs-text')}
            />
          ),
          alignVertically: 'middle',
        },
        {
          value: 'Tilbudskurs',
          sortable: true,
          alignVertically: 'middle',
          minWidth: 160,
        },
        {
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-fixed-tooltip-tilbudskurs-text')}
            />
          ),
          alignVertically: 'middle',
        },
        {
          value: 'Udbetalingskurs',
          suffix: 'Fastkursaftale ' + date1,
          minWidth: 190,
        },
        {
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-fixed-tooltip-fastkurs-text')}
            />
          ),
          alignVertically: 'middle',
        },
        {
          value: 'Udbetalingskurs',
          suffix: 'Fastkursaftale ' + date2,
          minWidth: 190,
        },
        {
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-fixed-tooltip-fastkurs-text')}
            />
          ),
          alignVertically: 'middle',
        },
        {
          value: 'Udbetalingskurs',
          suffix: 'Fastkursaftale ' + date3,
          minWidth: 190,
        },
        {
          value: (
            <Tooltip
              element={
                <IconWrapper size="24" color="Action">
                  <Icons.Action.Question />
                </IconWrapper>
              }
              content={t('kurser-fixed-tooltip-fastkurs-text')}
            />
          ),
          alignVertically: 'middle',
        },
      ],
      // Body rows:
      ...data.map((item): Cell[] => {
        return [
          {
            style: {
              width: 250,
            },
            prefix: item?.numberOfTermsWithoutRepayment
              ? parseFloat(item.numberOfTermsWithoutRepayment) === 0
                ? t('kurser-termsWithoutRepaymentEQ0-label') + ` (årgang ${item?.bondname.slice(-4)})`
                : parseFloat(item.numberOfTermsWithoutRepayment) === 40
                ? t('kurser-termsWithoutRepaymentEQ40-label') + ` (årgang ${item?.bondname.slice(-4)})`
                : t('kurser-termsWithoutRepaymentGT40-label') + ` (årgang ${item?.bondname.slice(-4)})`
              : '',
            value: `${item?.termToMaturityYears ? parseFloat(item.termToMaturityYears).toFixed(0) : 0} år / ${
              item?.nominelInterestRate ? formatAsCurrency(parseFloat(item.nominelInterestRate), 1, true) : 0
            } %`,
            suffix: item?.isinCode ? <IsinSuffix>{item.isinCode}</IsinSuffix> : '',
          },
          {
            style: {
              borderLeft: 0,
              minWidth: 110,
              position: 'relative',
            },
            value: (
              <>
                {item?.offerprice > 99.75 || item?.offerprice < 0 ? (
                  <ClosedBond>
                    {' '}
                    <span>{t('kurser-closedbond-information-label')}</span>{' '}
                  </ClosedBond>
                ) : null}
                <Link
                  href={void 0}
                  textColor={Color.red50}
                  underlineColorPrimary={Color.red50}
                  onClick={(e) => {
                    handleClick(item?.isinCode, item?.bondname);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleClick(item?.isinCode, item?.bondname);
                    }
                  }}
                  aria-label={t('kurser-showLoanDetails-label')}
                  tabIndex={0}
                  role="button"
                >
                  {t('kurser-showLoanDetails-label')}
                </Link>
              </>
            ),
            alignVertically: 'top',
          },
          {
            value: parseFloat(item?.prices?.[0]?.price.replace(/,/, '.')),
            data: {
              text:
                parseFloat(item?.prices?.[0]?.price.replace(/,/, '.')) < 0 ||
                item?.prices?.[0]?.price === '' ||
                item?.prices?.[0]?.price === 'undefined'
                  ? '-'
                  : (item?.prices?.[0]?.price).slice(0, -1),
            },
            cellValueRender: TableCellRender,
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
          {
            value: item?.offerprice,
            data: {
              text:
                item?.offerprice <= 0 || item?.offerprice === undefined
                  ? '-'
                  : formatAsCurrency(item.offerprice, 3, true),
            },
            cellValueRender: TableCellRender,
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
          // code changes made to show '-' whenever we get -ve value from backend.
          // backend would send -ve value when the API could not get bond prices for future dates.
          {
            value:
              parseFloat(item?.prices?.[1]?.price.replace(/,/, '.')) < 0 ||
              item?.prices?.[1]?.price === '' ||
              item?.prices?.[1]?.price === 'undefined'
                ? '-'
                : (item?.prices?.[1]?.price).slice(0, -1),
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
          {
            value:
              parseFloat(item?.prices?.[2]?.price.replace(/,/, '.')) < 0 ||
              item?.prices?.[2]?.price === '' ||
              item?.prices?.[2]?.price === 'undefined'
                ? '-'
                : (item?.prices?.[2]?.price).slice(0, -1),
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
          {
            value:
              parseFloat(item?.prices?.[3]?.price.replace(/,/, '.')) < 0 ||
              item?.prices?.[3]?.price === '' ||
              item?.prices?.[3]?.price === 'undefined'
                ? '-'
                : item?.prices?.[3]?.price?.slice(0, -1),
            alignVertically: 'middle',
          },
          {
            style: {
              borderLeft: 0,
              maxWidth: '1px',
            },
            value: null,
          },
        ];
      }),
    ];
  }, [data, t, handleClick]);

  return <DynamicTable striped={false} bordered={true} firstRowIsHead={true} data={tableData} />;
};

const ClosedBond = styled.div`
  width: 74px;
  height: 28px;
  padding: 3px;
  border-radius: 3px;
  text-align: center;
  border: solid 1px ${RddkTheme.colors.red50};
  background-color: rgba(226, 0, 26, 0.08);
  span {
    font-size: 12px;
    font-weight: 600;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.25;
    letter-spacing: 0.8px;
    text-align: center;
    color: ${RddkTheme.colors.red50};
  }
`;

const Link = styled(LinkAnimatedUnderline)`
  cursor: pointer;
  position: absolute;
  bottom: 21%;
  right: 19%;
  font-size: 12px;
  color: ${RddkTheme.colors.red50};
`;

// Styled-component to ensure our styles have higher specificity than any class
const IsinSuffix = styled.span`
  && {
    color: #111; // Nearly black for maximum contrast on white
    font-weight: 700;
    opacity: 1;
  }
`;
